import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { FiCheck } from 'react-icons/fi';

const SampleRequestModal = ({ quotation, order, onClose, onConfirm }) => {
  const [animate, setAnimate] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [sampleRequested, setSampleRequested] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const standardizedUnit = quotation?.standardizedUnit || "Kg";
  const packSizeUnit = quotation?.packSize?.unit || quotation?.standardizedUnit || "Kg";

  useEffect(() => { setTimeout(() => setAnimate(true), 10); }, []);

  const pricePerKg =
    quotation?.pricing?.commissionAdjustedAmount ||
    quotation?.pricing?.amount ||
    0;

  const samplePackSize =
    quotation?.sampleDetails?.packSize ||
    quotation?.sample_pack_size ||
    quotation?.packSize?.weight ||
    0;

  // Convert pack size weight to standardized unit (e.g. grams to Kg) for price calculation
  const getWeightInStandardUnit = (val, unit, stdUnit) => {
    const num = Number(val || 0);
    const u = String(unit || "").toLowerCase();
    const std = String(stdUnit || "").toLowerCase();

    if (u.includes("g") && !u.includes("kg") && std === "kg") {
      return num / 1000;
    }
    if (u.includes("ton") && std === "kg") {
      return num * 1000;
    }
    if (u.includes("quintal") && std === "kg") {
      return num * 100;
    }
    return num;
  };

  const sampleWeightInStandardUnit = getWeightInStandardUnit(
    samplePackSize,
    packSizeUnit,
    standardizedUnit
  );

  const fullOrderQty =
    quotation?.quantity?.volume ||
    quotation?.quantity ||
    0;

  const transportCharge = 0;

  const subtotal =
    (sampleRequested
      ? sampleWeightInStandardUnit
      : fullOrderQty) * pricePerKg;

  const total = subtotal + transportCharge;

  const formatPrice = (num) =>
    Number(num || 0).toLocaleString();

  const handleClose = () => { setAnimate(false); setTimeout(onClose, 250); };

  const handleConfirm = async () => {

    if (sampleRequested === null) {
      toast.error("Please select an option");
      return;
    }

    if (sampleRequested === false && !declarationAccepted) {
      toast.error("Please accept declaration");
      return;
    }

    // STEP 1 → STEP 2
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    // FINAL CONFIRM
    setLoading(true);

    try {
      await onConfirm(sampleRequested);
    } catch {
      toast.error("Failed to process");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
    // onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full md:w-[480px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"
          }`}
        style={{ maxHeight: "90vh" }}
      >
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center">
          <button
            onClick={() => {
              if (currentStep === 2) {
                setCurrentStep(1);
              } else {
                handleClose();
              }
            }}
            className="text-2xl mr-3"
          >
            ←
          </button>
          <div className="flex-1 font-semibold text-lg">Request Sample?</div>
        </div>

        <div className="p-5 overflow-auto flex-1">

          {/* STEP 1 */}
          {currentStep === 1 && (
            <div className="space-y-4">

              <p className="text-sm text-gray-500 leading-relaxed">
                Please choose whether you would like to request a product sample(you can add multiple samples to your cart)
                or proceed directly with the full order(Only one for full order).
              </p>

              {/* SAMPLE YES OPTION */}
              <div
                onClick={() => {
                  setSampleRequested(true);
                  setDeclarationAccepted(false);
                }}
                className={`rounded-2xl border-2 cursor-pointer overflow-hidden transition-all ${sampleRequested === true
                  ? "border-primaryColor bg-primaryColor/5"
                  : "border-gray-200 hover:border-gray-300"
                  }`}
              >
                {/* Option Card */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${sampleRequested === true
                        ? "border-primaryColor bg-primaryColor"
                        : "border-gray-300"
                        }`}
                    >
                      {sampleRequested === true && (
                        <FiCheck className="text-white text-xs" />
                      )}
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Yes, I need a sample
                      </h3>

                      <p className="text-sm text-gray-500 mt-1 leading-6">
                        This quotation will be added to your sample cart. You can add more quotations as samples. Once all transport charges are finalized by admin, you can proceed to payment.
                      </p>
                    </div>
                  </div>
                </div>

                {/* TIP BOX - ONLY SHOWS WHEN THIS OPTION IS SELECTED */}
                {sampleRequested === true && (
                  <div className="border-t border-primaryColor/20 bg-blue-50 p-4 space-y-2">
                    <p className="text-[11px] text-blue-700 font-medium leading-5 flex items-center gap-2">
                      <span>✓</span> Add multiple samples to your cart
                    </p>
                    <p className="text-[11px] text-blue-700 font-medium leading-5 flex items-center gap-2">
                      <span>✓</span> Wait for admin to set transport charges
                    </p>
                    <p className="text-[11px] text-blue-700 font-medium leading-5 flex items-center gap-2">
                      <span>✓</span> Proceed to payment once all charges are finalized
                    </p>
                  </div>
                )}
              </div>

              {/* FULL ORDER OPTION */}
              <div
                onClick={() => {
                  setSampleRequested(false);
                  setDeclarationAccepted(false);
                }}
                className={`rounded-2xl border-2 overflow-hidden cursor-pointer transition-all ${sampleRequested === false
                  ? "border-primaryColor bg-primaryColor/5"
                  : "border-gray-200 hover:border-gray-300"
                  }`}
              >
                {/* Option Card */}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${sampleRequested === false
                        ? "border-primaryColor bg-primaryColor"
                        : "border-gray-300"
                        }`}
                    >
                      {sampleRequested === false && (
                        <FiCheck className="text-white text-xs" />
                      )}
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900">
                        No, proceed for full order
                      </h3>

                      <p className="text-sm text-gray-500 mt-1 leading-6">
                        You will proceed directly with full order confirmation for this quotation based on the digital product verification provided by PCM Exports.
                      </p>
                    </div>
                  </div>
                </div>

                {/* DECLARATION BOX - ONLY SHOWS WHEN THIS OPTION IS SELECTED */}
                {sampleRequested === false && (
                  <div className="border-t border-primaryColor/20 bg-gray-50 p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Self Declaration
                    </h4>

                    <p className="text-xs text-gray-700 leading-6 mb-4">
                      I hereby declare that we are ready to confirm the full order for this quotation based on the photo and video provided by PCM Exports (Admin). We understand that digital photos and videos may not always accurately represent the actual color and size of the product.
                    </p>

                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeclarationAccepted(!declarationAccepted);
                      }}
                      className="flex items-start gap-3 cursor-pointer"
                    >
                      <div
                        className={`w-5 h-5 mt-0.5 border rounded-md flex items-center justify-center transition-all flex-shrink-0 ${declarationAccepted
                          ? "bg-primaryColor border-primaryColor"
                          : "border-gray-300 bg-white"
                          }`}
                      >
                        {declarationAccepted && (
                          <FiCheck className="text-white text-[11px]" />
                        )}
                      </div>

                      <span className="text-xs text-gray-700 leading-5">
                        I am ready to proceed with full order confirmation and payment as per the GST invoice.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {currentStep === 2 && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5">

              {/* Quantity */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    {sampleRequested
                      ? "Sample Pack Size"
                      : "Order Quantity"}
                  </p>
                </div>

                <p className="text-sm font-semibold text-gray-900">
                  {sampleRequested
                    ? `${samplePackSize} ${packSizeUnit}`
                    : `${fullOrderQty} ${standardizedUnit}`
                  }
                </p>
              </div>

              <div className="my-4 border-t border-dashed border-gray-200" />

              {/* Price */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">
                    {sampleRequested
                      ? "Sample Price"
                      : "Product Price"}
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    ₹{formatPrice(pricePerKg)} / {standardizedUnit}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    ₹{formatPrice(subtotal)}
                  </p>

                  <p className="text-xs text-gray-400 mt-1">
                    {(sampleRequested
                      ? `${sampleWeightInStandardUnit} ${standardizedUnit}`
                      : `${fullOrderQty} ${standardizedUnit}`)} × ₹{formatPrice(pricePerKg)}
                  </p>
                </div>
              </div>

              <div className="my-4 border-t border-dashed border-gray-200" />

              {/* Transport */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500 font-medium">
                  Transport Charges
                </p>

                <div className="px-2 py-1 rounded-lg bg-yellow-50 border border-yellow-200">
                  <span className="text-[10px] font-bold text-yellow-700">
                    {sampleRequested ? "PENDING FINALIZATION" : "INCLUDED"}
                  </span>
                </div>
              </div>

              <p className="text-[11px] text-gray-400 italic mt-2">
                {sampleRequested
                  ? "Admin will finalize this after your sample request is processed."
                  : "Transport charges are calculated based on your location."}
              </p>

              <div className="my-4 border-t border-dashed border-gray-200" />

              {/* Total */}
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-gray-900">
                  {sampleRequested ? "Sample Subtotal" : "Order Total"}
                </p>

                <p className="text-base font-extrabold text-primaryColor">
                  ₹{formatPrice(total)} {sampleRequested && "+ To Be Determined"}
                </p>
              </div>

            </div>
          )}
        </div>
        <div className="p-5 border-t flex gap-3">
          <button onClick={handleClose} disabled={loading} className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={
            loading ||
            sampleRequested === null ||
            (sampleRequested === false && !declarationAccepted)
          }
            className="flex-1 py-3 bg-primaryColor hover:bg-primaryBtnHoverColor text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {loading
              ? "Processing..."
              : currentStep === 1
                ? "Continue"
                : sampleRequested
                  ? "Add to Sample Cart"
                  : "Proceed with Full Order"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SampleRequestModal